package or.garden.manager.features.fertilizer

import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import karika.distribucija.ba.domain.model.ResultState
import or.garden.manager.components.FertilizerDetailsModal
import or.garden.manager.components.SnackbarType
import or.garden.manager.domain.NaturalFertilizerService
import or.garden.manager.domain.model.FertilizerStatus
import or.garden.manager.domain.model.FertilizerType
import or.garden.manager.domain.model.NaturalFertilizer
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.H2
import org.jetbrains.compose.web.dom.H3
import org.jetbrains.compose.web.dom.I
import org.jetbrains.compose.web.dom.Span
import org.jetbrains.compose.web.dom.Text

@Composable
fun NaturalFertilizerScreen(
    onAdd: () -> Unit,
    onEdit: (Int) -> Unit,
    showSnackbar: (String, SnackbarType) -> Unit
) {
    val userRole = or.garden.manager.domain.GardenHttpClient.role
    val isAdmin = userRole == "ADMIN"

    var fertilizers by remember { mutableStateOf<List<NaturalFertilizer>>(emptyList()) }
    var isLoading by remember { mutableStateOf(true) }
    var selectedFertilizer by remember { mutableStateOf<NaturalFertilizer?>(null) }

    fun fetchFertilizers() {
        isLoading = true
        NaturalFertilizerService.getAll { state ->
            when (state) {
                is ResultState.Success -> {
                    fertilizers = state.data.data ?: emptyList()
                    isLoading = false
                }

                is ResultState.Error -> {
                    showSnackbar("Greška: ${state.message}", SnackbarType.ERROR)
                    isLoading = false
                }

                else -> {}
            }
        }
    }

    LaunchedEffect(Unit) {
        fetchFertilizers()
    }

    val userFertilizers = fertilizers.filter { !it.isAdminCreated }
    val recommendedFertilizers = fertilizers.filter { it.isAdminCreated }

    Div({ classes("p-6", "max-w-7xl", "mx-auto") }) {
        if (isLoading) {
            Div({ classes("flex", "justify-center", "py-20") }) {
                I({ classes("fas", "fa-circle-notch", "fa-spin", "text-4xl", "text-emerald-500") })
            }
        } else if (fertilizers.isEmpty()) {
            Div({
                classes(
                    "text-center",
                    "py-20",
                    "bg-white",
                    "rounded-3xl",
                    "border",
                    "border-dashed",
                    "border-gray-300"
                )
            }) {
                I({ classes("fas", "fa-flask", "text-5xl", "text-gray-300", "mb-4") })
                H3({
                    classes(
                        "text-xl",
                        "font-medium",
                        "text-gray-900"
                    )
                }) { Text("Nema pronađenih đubriva") }
            }
        } else {
            // User's Fertilizers
            if (userFertilizers.isNotEmpty()) {
                H2({
                    classes(
                        "text-2xl",
                        "font-bold",
                        "text-[#134130]",
                        "mb-6"
                    )
                }) { Text("Moja đubriva") }
                Div({
                    classes(
                        "grid",
                        "grid-cols-1",
                        "md:grid-cols-2",
                        "lg:grid-cols-3",
                        "gap-6",
                        "mb-12"
                    )
                }) {
                    userFertilizers.forEach { fertilizer ->
                        FertilizerCard(
                            fertilizer = fertilizer,
                            onClick = {
                                selectedFertilizer = fertilizer
                            }
                        )
                    }
                }
            }

            // Recommended Fertilizers
            if (recommendedFertilizers.isNotEmpty()) {
                H2({
                    classes(
                        "text-2xl",
                        "font-bold",
                        "text-[#134130]",
                        "mb-6"
                    )
                }) { Text("Garden đubriva (Preporučeno)") }
                Div({
                    classes(
                        "grid",
                        "grid-cols-1",
                        "md:grid-cols-2",
                        "lg:grid-cols-3",
                        "gap-6"
                    )
                }) {
                    recommendedFertilizers.forEach { fertilizer ->
                        FertilizerCard(
                            fertilizer = fertilizer,
                            onClick = {
                                selectedFertilizer = fertilizer
                            }
                        )
                    }
                }
            }
        }
    }

    selectedFertilizer?.let { fertilizer ->
        val isOwner = !fertilizer.isAdminCreated || isAdmin
        FertilizerDetailsModal(
            fertilizer = fertilizer,
            showEditButton = isOwner,
            onEdit = {
                onEdit(fertilizer.id!!)
                selectedFertilizer = null
            },
            onDelete = {
                NaturalFertilizerService.delete(fertilizer.id!!) { state ->
                    when (state) {
                        is ResultState.Success -> {
                            showSnackbar("Đubrivo uspješno obrisano", SnackbarType.SUCCESS)
                            fetchFertilizers()
                        }
                        is ResultState.Error -> {
                            showSnackbar("Greška pri brisanju: ${state.message}", SnackbarType.ERROR)
                        }
                        else -> {}
                    }
                }
                selectedFertilizer = null
            },
            onClose = { selectedFertilizer = null }
        )
    }
}

@Composable
fun FertilizerCard(
    fertilizer: NaturalFertilizer,
    onClick: () -> Unit
) {
    Div({
        classes(
            "bg-white",
            "rounded-2xl",
            "p-6",
            "border",
            "border-gray-100",
            "shadow-sm",
            "hover:shadow-md",
            "transition-all",
            "cursor-pointer"
        )
        onClick { onClick() }
    }) {
        Div({ classes("flex", "justify-between", "items-start", "mb-4") }) {
            Div({
                classes(
                    "w-12",
                    "h-12",
                    "rounded-xl",
                    "bg-emerald-50",
                    "flex",
                    "items-center",
                    "justify-center",
                    "text-emerald-600"
                )
            }) {
                I({ classes("fas", "fa-seedling") })
            }
            StatusBadge(fertilizer.status)
        }

        H3({ classes("text-lg", "font-bold", "text-[#134130]", "mb-1") }) { Text(fertilizer.name) }

        Div({ classes("flex", "items-center", "gap-2", "text-sm", "text-gray-500", "mb-4") }) {
            I({ classes("fas", "fa-clock", "text-xs") })
            val typeText = when (fertilizer.type) {
                FertilizerType.FERMENTATION -> "Fermentacija (14 dana)"
                FertilizerType.TEA -> "Biljni čaj (24h)"
                FertilizerType.CHEMICAL_REACTION -> "Hemijska reakcija"
            }
            Text(typeText)
        }

        if (fertilizer.dilutionRatio != null) {
            Div({ classes("p-3", "bg-gray-50", "rounded-lg", "text-sm", "mt-auto") }) {
                Div({ classes("font-semibold", "text-gray-700") }) { Text("Omjer razblaživanja:") }
                Div({ classes("text-emerald-700") }) { Text(fertilizer.dilutionRatio ?: "-") }
            }
        }
    }
}

@Composable
fun StatusBadge(status: FertilizerStatus) {
    val (bgColor, textColor, label) = when (status) {
        FertilizerStatus.PREPARING -> Triple("bg-blue-50", "text-blue-600", "U pripremi")
        FertilizerStatus.READY -> Triple("bg-emerald-50", "text-emerald-600", "Spremno")
        FertilizerStatus.EXPIRED -> Triple("bg-red-50", "text-red-600", "Isteklo")
        FertilizerStatus.ARCHIVED -> Triple("bg-gray-50", "text-gray-600", "Arhivirano")
    }

    Span({
        classes("px-3", "py-1", "rounded-full", "text-xs", "font-bold", bgColor, textColor)
    }) { Text(label) }
}
